import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AdvisorGoalNav } from "@/components/flow/advisor-goal-nav";
import { FlowNavigationProvider } from "@/components/flow/flow-navigation-context";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { apiErrorMessage } from "@/lib/api/_client";
import * as flowInstancesApi from "@/lib/api/flow-instances";
import { flowInstanceQueryKeys } from "@/lib/hooks/use-flow-instances";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useInvalidateFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useApplyGoalRiskInheritance } from "@/lib/hooks/use-flow-instances/use-goal-risk-inheritance";
import { useT } from "@/lib/i18n/use-t";
import type { FlowPage } from "@/lib/types/flow";
import type { GoalListItem } from "@/lib/types/goal";
import { T } from "./goal-list-widget.i18n";

type GoalListWidgetProps = {
  flowInstanceId: string;
  flowLanguage: string;
  goalPages: FlowPage[];
  portfolioPages: FlowPage[];
  /** QL-120 — per-goal / per-portfolio page-level conditional visibility. */
  isGoalPageVisible?: (page: FlowPage, goalId: string) => boolean;
  isPortfolioPageVisible?: (page: FlowPage, goalId: string, portfolioId: string) => boolean;
  globalPagesBefore: FlowPage[];
  globalPagesAfter: FlowPage[];
  multiPortfolio: boolean;
  /** QL-593 — the flow's goal cap; the server enforces it, this only hides "Add Goal". */
  maxGoals: number;
  isCompleted: boolean;
  // Content render-props threaded down to AdvisorGoalNav → GoalContextView.
  renderGoalPage: (page: FlowPage, goalId: string) => React.ReactNode;
  renderPortfolioPage: (page: FlowPage, goalId: string, portfolio: any) => React.ReactNode;
  renderGlobalPage: (page: FlowPage) => React.ReactNode;
  // Goals are passed from parent (fetched via React Query in the wizard)
  goals: GoalListItem[];
  // Follow-up A (H1) — error highlighting on tab surfaces.
  showValidationErrors?: boolean;
  markers: CompletionMarkers;
};

/**
 * Goal List Widget
 *
 * Manages multi-goal tab UI for the flow wizard.
 * Goals come from the parent via React Query; mutations invalidate the shared query.
 */
export function GoalListWidget({
  flowInstanceId,
  flowLanguage,
  goalPages,
  portfolioPages,
  isGoalPageVisible,
  isPortfolioPageVisible,
  globalPagesBefore,
  globalPagesAfter,
  multiPortfolio,
  maxGoals,
  isCompleted,
  renderGoalPage,
  renderPortfolioPage,
  renderGlobalPage,
  goals,
  showValidationErrors,
  markers,
}: GoalListWidgetProps) {
  const t = useT(T);
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const applyRiskInheritance = useApplyGoalRiskInheritance(flowInstanceId);

  // Active-goal state — owned here, passed down as a controlled prop. Per-goal page selection now
  // lives inside GoalContextView, so the widget no longer tracks a page index.
  const [currentGoalId, setCurrentGoalId] = useState<string | null>(null);
  // Ref tracks currentGoalId synchronously so the guard effect can read it without depending on
  // the state value (which would race with not-yet-refetched goal lists after create).
  const currentGoalIdRef = useRef<string | null>(null);

  // Confirm delete dialog
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<string | null>(null);

  // Wrapper that keeps state and ref in sync.
  const setCurrentGoal = (id: string | null) => {
    setCurrentGoalId(id);
    currentGoalIdRef.current = id;
  };

  // Initialize current goal on first load
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current || goals.length === 0) return;
    hasInitialized.current = true;
    if (globalPagesBefore.length === 0) {
      setCurrentGoal(goals[0].id);
    }
  }, [goals, globalPagesBefore.length]);

  // Safety: if current goal was deleted, switch to first remaining.
  // Only depends on `goals` (not `currentGoalId`); reads from the ref so it won't fire when
  // setCurrentGoal sets a newly-created goal ID that hasn't appeared in the stale list yet.
  useEffect(() => {
    const id = currentGoalIdRef.current;
    if (id && goals.length > 0 && !goals.some((g) => g.id === id)) {
      setCurrentGoal(goals[0].id);
    }
  }, [goals]);

  const createGoalMutation = useMutation({
    mutationFn: (data: Parameters<typeof flowInstancesApi.createGoal>[1]) =>
      flowInstancesApi.createGoal(flowInstanceId, data),
    onSuccess: (newGoal) => {
      queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.goals(flowInstanceId) });
      // New goal grows the engine's fan-out — its goal_input, goal-scoped fields, and
      // per-goal components all become required. Refresh markers so highlighting catches up
      // when a goal is added after a failed submit.
      invalidateCompletion(flowInstanceId);
      // QL-212 (D13) — a goal born scored under a client-level risk model moves the
      // same queries a risk submit does.
      applyRiskInheritance(newGoal.id, newGoal.riskInheritance);
      setCurrentGoal(newGoal.id);
    },
    onError: (err) => {
      // QL-593 — the server's cap wins over the button (a double-click at the
      // last free slot, or a cap lowered while the wizard is open); its
      // max_goals_reached message carries the current cap.
      toast.error(t.error, { description: apiErrorMessage(err, t.failedToCreateGoal) });
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: (goalId: string) => flowInstancesApi.deleteGoal(goalId),
    onSuccess: (_result, deletedGoalId) => {
      toast.success(t.goalDeletedSuccessfully);
      // Switch away from deleted goal immediately
      if (currentGoalId === deletedGoalId) {
        const remaining = goals.filter((g) => g.id !== deletedGoalId);
        setCurrentGoal(remaining.length > 0 ? remaining[0].id : null);
      }
      queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.goals(flowInstanceId) });
      // Deleting a goal shrinks the engine's fan-out — its missing requirements drop off.
      // Refresh so D14's auto-clear can fire if this removed the last missing item.
      invalidateCompletion(flowInstanceId);
    },
    onError: (err) => {
      toast.error(t.error, { description: apiErrorMessage(err, t.failedToDeleteGoal) });
    },
  });

  const handleAddGoal = async () => {
    if (goals.length >= maxGoals) {
      toast.error(t.maxGoalsReached, {
        description: t.maxGoalsDescription(maxGoals),
      });
      return;
    }
    // No goalType: the service applies its default, and a collection with goal
    // types would reject a hard-coded value. The goal page's type picker is
    // where the type gets chosen. No name either: the service names it in the
    // flow language (QL-710).
    createGoalMutation.mutate({
      portfolioId: undefined,
      lang: flowLanguage,
    });
  };

  const handleDeleteGoal = (goalId: string) => {
    if (goals.length === 1) {
      toast.error(t.cannotDeleteLastGoal, { description: t.cannotDeleteLastGoalDescription });
      return;
    }
    setGoalToDelete(goalId);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!goalToDelete) return;
    deleteGoalMutation.mutate(goalToDelete);
    setGoalToDelete(null);
  };

  return (
    <FlowNavigationProvider onGoalChange={setCurrentGoal}>
      <AdvisorGoalNav
        instanceId={flowInstanceId}
        goals={goals}
        goalPages={goalPages}
        portfolioPages={portfolioPages}
        isGoalPageVisible={isGoalPageVisible}
        isPortfolioPageVisible={isPortfolioPageVisible}
        globalPagesBefore={globalPagesBefore}
        globalPagesAfter={globalPagesAfter}
        multiPortfolio={multiPortfolio}
        isCompleted={isCompleted}
        canAddGoal={goals.length < maxGoals && !createGoalMutation.isPending}
        onAddGoal={handleAddGoal}
        onDeleteGoal={handleDeleteGoal}
        currentGoalId={currentGoalId}
        onCurrentGoalChange={setCurrentGoal}
        renderGoalPage={renderGoalPage}
        renderPortfolioPage={renderPortfolioPage}
        renderGlobalPage={renderGlobalPage}
        showValidationErrors={showValidationErrors}
        markers={markers}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteGoal}
        description={t.deleteGoalDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />
    </FlowNavigationProvider>
  );
}
